import { breakpoints } from '@/lib/utils';
import { Component, defineComponent } from '@decify/cell-shopify/client';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import './Slider.scss';
export const sliderName = 'Slider';
export const sliderTagName = 'cell-component-slider';
export const sliderSlideChangeEvent = 'slideChange';
export class Slider extends Component {
    classNames = {
        base: 'Slider',
        slider: 'Slider__Slider',
        controls: 'Slider__Controls',
        pagination: 'Slider__Pagination',
        navigationButton: 'Slider__NavigationButton',
    };
    slider;
    sliderOptions;
    constructor() {
        super(sliderName);
        this.handleResize = this.handleResize.bind(this);
        this.handleProgress = this.handleProgress.bind(this);
        this.handleSlideChange = this.handleSlideChange.bind(this);
        this.slider = null;
        this.sliderOptions = {
            containerModifierClass: 'Slider--',
            wrapperClass: 'Slider__Wrapper',
            slideClass: 'SlideItem',
            slideActiveClass: 'SlideItem--Active',
            slideNextClass: 'SlideItem--Next',
            slidePrevClass: 'SlideItem--Prev',
            modules: [Autoplay, Navigation, Pagination],
            // TODO: Make this work.
            // direction: 'vertical',
            on: {
                init: (swiper) => this.handleResize(swiper),
                resize: (swiper) => this.handleResize(swiper),
                progress: (swiper, progress) => this.handleProgress(progress, swiper.params.loop),
                slideChange: (swiper) => this.handleSlideChange(swiper.activeIndex),
            },
            autoplay: true,
            grabCursor: true,
            centerInsufficientSlides: true,
            breakpoints: Object.entries(breakpoints).reduce((acc, [breakpointKey, breakpointValue]) => {
                acc[breakpointValue] = {
                    enabled: this.props.enabled[breakpointKey] ?? true,
                    slidesPerView: this.props.slidesPerView[breakpointKey] ?? 1,
                    spaceBetween: this.props.spaceBetween[breakpointKey] ?? 0,
                    autoplay: this.props.autoplay[breakpointKey]
                        ? {
                            delay: this.props.autoplayDelay[breakpointKey],
                            pauseOnMouseEnter: true,
                        }
                        : false,
                    loop: this.props.loop[breakpointKey] ?? false,
                    navigation: this.props.navigation[breakpointKey] && this.navigationElements
                        ? {
                            disabledClass: 'Slider__NavigationButton--Disabled',
                            prevEl: this.navigationElements.prev,
                            nextEl: this.navigationElements.next,
                        }
                        : false,
                    pagination: this.props.pagination[breakpointKey] && this.paginationElement
                        ? {
                            paginationDisabledClass: 'Slider__Pagination--Disabled',
                            bulletClass: 'Slider__PaginationBullet',
                            bulletActiveClass: 'Slider__PaginationBullet--Active',
                            modifierClass: 'Slider__Pagination--',
                            horizontalClass: 'Slider__Pagination--Horizontal',
                            el: this.paginationElement,
                            clickable: true,
                        }
                        : false,
                };
                return acc;
            }, {}),
        };
    }
    componentDidMount() {
        // TODO: Lazyoload swiper
        this.slider = new Swiper(this.sliderElement, this.sliderOptions);
    }
    handleResize(swiper) {
        const hasMoreSlidesThanVisible = swiper.slides.length > Number(swiper.params.slidesPerView);
        if (this.navigationElements) {
            this.navigationElements.prev.style.display = hasMoreSlidesThanVisible ? '' : 'none';
            this.navigationElements.next.style.display = hasMoreSlidesThanVisible ? '' : 'none';
        }
        if (this.controlsElements) {
            this.controlsElements.style.display = hasMoreSlidesThanVisible ? '' : 'none';
        }
    }
    handleProgress(progress, loop) {
        if (loop)
            return;
        // Clamp progress between 0 and 1
        const clampedProgress = Math.min(1, Math.max(0, progress));
        if (clampedProgress === 0) {
            this.navigationElements?.prev.querySelector('button')?.setAttribute('disabled', '');
        }
        else {
            this.navigationElements?.prev.querySelector('button')?.removeAttribute('disabled');
        }
        if (clampedProgress === 1) {
            this.navigationElements?.next.querySelector('button')?.setAttribute('disabled', '');
        }
        else {
            this.navigationElements?.next.querySelector('button')?.removeAttribute('disabled');
        }
    }
    handleSlideChange(activeIndex) {
        this.publish(sliderSlideChangeEvent, {
            activeIndex,
        });
    }
    get sliderElement() {
        const sliderElement = this.querySelector('.Slider__Slider');
        if (!sliderElement || !(sliderElement instanceof HTMLElement)) {
            throw new Error('Slider element not found');
        }
        return sliderElement;
    }
    get controlsElements() {
        const controlsElement = this.querySelector('.Slider__Controls');
        if (controlsElement && !(controlsElement instanceof HTMLElement)) {
            throw new Error('Controls element is not an HTMLElement');
        }
        return controlsElement;
    }
    get navigationElements() {
        const prevNavigationButtonElement = this.querySelector('.Slider__NavigationButton--Prev');
        const nextNavigationButtonElement = this.querySelector('.Slider__NavigationButton--Next');
        if (!prevNavigationButtonElement || !nextNavigationButtonElement) {
            return null;
        }
        if (!(prevNavigationButtonElement instanceof HTMLElement) ||
            !(nextNavigationButtonElement instanceof HTMLElement)) {
            throw new Error('Navigation button elements are not HTMLElements');
        }
        return {
            prev: prevNavigationButtonElement,
            next: nextNavigationButtonElement,
        };
    }
    get paginationElement() {
        const paginationElement = this.querySelector('.Slider__Pagination');
        if (paginationElement && !(paginationElement instanceof HTMLElement)) {
            throw new Error('Pagination element is not an HTMLElement');
        }
        return paginationElement;
    }
}
defineComponent(sliderTagName, Slider);
